import { Percent, TradeType } from '@kodiak-finance/sdk-core'
import AnimatedDropdown from 'components/AnimatedDropdown'
import CurrencyLogo from 'components/CurrencyLogo'
import { LoadingOpacityContainer } from 'components/Loader/styled'
import { LogoRenderer } from 'components/Logo/LogoRenderer'
import QuestionHelper from 'components/QuestionHelper'
import RoutingDiagram from 'components/RoutingDiagram/RoutingDiagram'
import { MouseoverTooltipContent } from 'components/Tooltip'
import { useTokens } from 'hooks/Tokens'
import { formatSwapAmount } from 'lib/utils/formatAmount'
import { ReactNode, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ChevronDown } from 'react-feather'
import { Trade } from 'state/routing/trades'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'
import { opacify } from 'theme/utils'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { warningSeverity } from 'utils/prices'
import { formatUnits } from 'viem'

import KXLogo from '../../assets/images/kx.png'
import { StyledGasIcon } from './GasEstimateBadge'
import { ResponsiveTooltipContainer } from './styleds'
import TradePrice from './TradePrice'

// Matches the near-square corners of the swap card, inputs and token selectors around the plate
const RADIUS = '4px'

export const WarningSection = styled.div`
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 0.75rem;
`

export const WarningContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: rgb(251, 191, 36);
`

const Container = styled.div`
  width: 100%;
  margin-top: 0.75rem;
  border: 1px solid ${({ theme }) => theme.buttonSubtle1};
  border-radius: ${RADIUS};
  background: ${({ theme }) => theme.cardSecondary};
  overflow: hidden;
`

// A div, not a button: the price inside is its own button for flipping the rate
const Header = styled.div<{ $interactive: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accentAction};
    outline-offset: -2px;
  }
`

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 0.8125rem;
`

const GasLine = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
`

// Same as production: the kX mark in a bordered box with the chevron beside it
const ProviderChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const LogoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: 1px solid ${({ theme }) => theme.cardBorderSecondary};
  border-radius: ${RADIUS};
`

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.textSecondary};
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.2s ease;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.cardBorderPrimary};
`

const DetailRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8125rem;
`

const DetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.textSecondary};
`

const DetailValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.textPrimary};
  font-weight: 500;
`

const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: ${RADIUS};
  background: rgba(251, 191, 36, 0.08);
  color: rgb(251, 191, 36);
  font-size: 0.75rem;
  line-height: 1.4;
`

const TooltipParagraphs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  line-height: 1.4;

  p {
    margin: 0;
  }
`

const LearnMore = styled.a`
  color: ${({ theme }) => theme.accentAction};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

// Reads as one more detail row: same type, and the 8px row rhythm instead of the 12px section gap
const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: -4px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.textSecondary};
`

// Rows bleed into the body gutter so their content lines up with the detail rows above
// One grid shared by every row (via subgrid), so the name column takes the longest name's width
// and the percentages after it line up vertically
const ProviderList = styled.div`
  display: grid;
  grid-template-columns: 26px max-content max-content 1fr;
  column-gap: 10px;
  margin: -4px -8px 0;
`

const ProviderRow = styled.div<{ $best?: boolean; $inactive?: boolean }>`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  padding: 8px 7px;
  border: 1px solid ${({ $best, theme }) => ($best ? theme.accentAction : 'transparent')};
  border-radius: ${RADIUS};
  background: ${({ $best }) => ($best ? 'rgba(255, 255, 255, 0.04)' : 'transparent')};
  line-height: 1.3;
  opacity: ${({ $inactive }) => ($inactive ? 0.5 : 1)};
`

const ViaProvider = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`

const NameLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`

const Diff = styled.span<{ $tone: 'better' | 'worse' | 'muted' }>`
  justify-self: start;
  box-sizing: border-box;
  // Wide enough for "−0.00%", so "Best" and the percentages form one even column of boxes
  min-width: 58px;
  text-align: center;
  padding: 2px 6px;
  border-radius: ${RADIUS};
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  ${({ $tone, theme }) => {
    const color =
      $tone === 'worse' ? theme.accentFailure : $tone === 'muted' ? theme.textSecondary : theme.accentSuccess
    return `color: ${color}; background: ${opacify(10, color)}; border: 1px solid ${opacify(30, color)};`
  }}
`

const Output = styled.div`
  justify-self: end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-variant-numeric: tabular-nums;
`

const Amount = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const OutputMeta = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.textSecondary};
`

const SmallGasIcon = styled(StyledGasIcon)`
  width: 12px;
  height: 12px;
  margin: 0 2px 0 0;
`

const Spinner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.backgroundInteractive};
  border-top-color: ${({ theme }) => theme.textSecondary};
  animation: ${keyframes`to { transform: rotate(360deg); }`} 0.8s linear infinite;
`

const percentChange = (value: number, base: number) => ((value - base) / base) * 100

// What the user receives in dollars after gas; the fallback when the backend sent no ranking score.
// Without USD prices for both routes it compares raw output.
function netDiffPercent(trade: Trade, bestTrade: Trade): number {
  const outputUSD = Number(trade.outputAmountUSD)
  const bestOutputUSD = Number(bestTrade.outputAmountUSD)
  if (!outputUSD || !bestOutputUSD) {
    return percentChange(parseFloat(trade.outputAmount.toExact()), parseFloat(bestTrade.outputAmount.toExact()))
  }
  const net = (output: number, candidate: Trade) => output - (Number(candidate.gasUseEstimateUSDFormatted) || 0)
  return percentChange(net(outputUSD, trade), net(bestOutputUSD, bestTrade))
}

// Compares on the backend's own ranking score, so the gap shown is the one that made it pick KX's route
function diffFromBest(trade: Trade, bestTrade: Trade): number {
  // A non-positive base (gas above the whole output) would flip or blow up the percentage
  if (trade.rankValue !== undefined && bestTrade.rankValue !== undefined && bestTrade.rankValue > 0) {
    return percentChange(trade.rankValue, bestTrade.rankValue)
  }
  return netDiffPercent(trade, bestTrade)
}

function DiffLabel({ trade, bestTrade }: { trade: Trade; bestTrade: Trade }) {
  const diff = diffFromBest(trade, bestTrade)
  // An empty cell, not null, keeps the output in the last grid column
  if (!Number.isFinite(diff)) return <span />

  const shown = Math.abs(diff).toFixed(2)
  // Decided on the shown precision, so a sub-cent gap reads as a neutral 0.00% rather than a colored one
  if (Number(shown) === 0) return <Diff $tone="muted">0.00%</Diff>

  return (
    <Diff $tone={diff > 0 ? 'better' : 'worse'}>
      {diff > 0 ? '+' : '−'}
      {shown}%
    </Diff>
  )
}

function RouteOutput({ trade }: { trade: Trade }) {
  return (
    <Output>
      <Amount>{formatSwapAmount(trade.outputAmount)}</Amount>
      <OutputMeta>
        <Gas usd={trade.gasUseEstimateUSDFormatted} />
      </OutputMeta>
    </Output>
  )
}

function Gas({ usd }: { usd: string }) {
  return (
    <GasLine>
      <SmallGasIcon />
      {formatDollarAmount(usd)}
    </GasLine>
  )
}

// MouseoverTooltipContent pads its child and zeroes its line height, which breaks row alignment
function WithTooltip({
  enabled,
  content,
  placement,
  children,
}: {
  enabled: boolean
  content: ReactNode
  placement: 'top' | 'bottom'
  children: ReactNode
}) {
  if (!enabled) return <>{children}</>
  return (
    <MouseoverTooltipContent wrap={false} content={content} placement={placement}>
      {children}
    </MouseoverTooltipContent>
  )
}

type RouteRow = { trade: Trade; available: boolean }

// Quotes refresh every 15s; while the list is open that would reshuffle rows and drop providers
// under the cursor, so the order is pinned when it opens and vanished routes stay as inactive rows
function useStableRoutes(ranked: Trade[], pinned: boolean, resetKey: string | undefined): RouteRow[] {
  const memory = useRef<{ key?: string; order: string[]; lastSeen: Map<string, Trade> }>({
    order: [],
    lastSeen: new Map(),
  })

  // Mutating the ref here is idempotent for the same inputs, so a repeated render converges to the same rows
  return useMemo(() => {
    if (!pinned || memory.current.key !== resetKey) {
      memory.current = { key: resetKey, order: [], lastSeen: new Map() }
    }
    const { order, lastSeen } = memory.current
    for (const trade of ranked) {
      lastSeen.set(trade.id, trade)
      if (!order.includes(trade.id)) order.push(trade.id)
    }

    const current = new Map(ranked.map((trade) => [trade.id, trade]))
    return order.flatMap((id) => {
      const trade = current.get(id) ?? lastSeen.get(id)
      return trade ? [{ trade, available: current.has(id) }] : []
    })
  }, [ranked, pinned, resetKey])
}

function formatRefFee(refFee: string | undefined, decimals = 18): string {
  try {
    return parseFloat(formatUnits(BigInt(refFee ?? 0), decimals)).toFixed(2)
  } catch {
    return '0.00'
  }
}

function Detail({ label, help, children }: { label: string; help: string; children: ReactNode }) {
  return (
    <DetailRow>
      <DetailLabel>
        {label}
        <QuestionHelper text={<ThemedText.DeprecatedBody fontSize={14}>{help}</ThemedText.DeprecatedBody>} />
      </DetailLabel>
      <DetailValue>{children}</DetailValue>
    </DetailRow>
  )
}

interface SwapDetailsDropdownProps {
  trade: Trade | undefined
  syncing: boolean
  loading: boolean
  showInverted: boolean
  setShowInverted: React.Dispatch<React.SetStateAction<boolean>>
  allowedSlippage: Percent
  trades: Trade[]
  selectedTrade: Trade | undefined
  priceImpact: Percent | undefined
}

export default function SwapDetailsDropdown({
  trade,
  syncing,
  loading,
  showInverted,
  setShowInverted,
  allowedSlippage,
  trades,
  selectedTrade,
  priceImpact,
}: SwapDetailsDropdownProps) {
  const [inputToken, outputToken] = useTokens([
    trade?.inputAmount.currency.wrapped.address,
    trade?.outputAmount.currency.wrapped.address,
  ])
  const [open, setOpen] = useState(false)

  const bestTrade = trades.find((candidate) => candidate.isBestPrice)

  const currentTrade = useMemo(
    () =>
      selectedTrade ??
      trades.find((candidate) => candidate.isBestPrice && candidate.canExecute) ??
      trades.find((candidate) => candidate.canExecute) ??
      bestTrade,
    [selectedTrade, trades, bestTrade]
  )

  const competitors = useMemo(() => {
    if (!bestTrade) return []
    const score = (candidate: Trade) => {
      const diff = diffFromBest(candidate, bestTrade)
      return Number.isFinite(diff) ? diff : -Infinity
    }
    return trades.filter((candidate) => candidate !== bestTrade).sort((a, b) => score(b) - score(a))
  }, [trades, bestTrade])

  const routesKey = trade
    ? `${trade.inputAmount.currency.wrapped.address}:${trade.outputAmount.currency.wrapped.address}:${trade.inputAmount.quotient}`
    : undefined
  const pinnedCompetitors = useStableRoutes(competitors, open, routesKey)
  // A pinned competitor that has since become the winner already appears as the kX row
  const competitorRows = pinnedCompetitors.filter(({ trade: competitor }) => competitor.id !== bestTrade?.id)

  if (loading && !trade) {
    return (
      <Container>
        <Header $interactive={false}>
          <GasLine>
            <Spinner style={{ marginRight: 8 }} />
            Fetching best price…
          </GasLine>
        </Header>
      </Container>
    )
  }

  const isExactIn = trade?.tradeType === TradeType.EXACT_INPUT
  // Only some providers (today just Kodiak) return their pools; for the rest a diagram would be a single bare hop
  const hasRoute = Boolean(currentTrade?.routingDiagramInfo?.length)

  return (
    <Container>
      <Header
        $interactive={Boolean(trade)}
        role="button"
        tabIndex={trade ? 0 : -1}
        aria-expanded={open}
        onClick={() => trade && setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (trade && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            setOpen((value) => !value)
          }
        }}
      >
        <LoadingOpacityContainer $loading={syncing}>
          <PriceColumn>
            {trade && (
              <TradePrice price={trade.executionPrice} showInverted={showInverted} setShowInverted={setShowInverted} />
            )}
            {currentTrade && <Gas usd={currentTrade.gasUseEstimateUSDFormatted} />}
          </PriceColumn>
        </LoadingOpacityContainer>

        {currentTrade && (
          <WithTooltip
            enabled={hasRoute && !open}
            placement="bottom"
            content={
              <ResponsiveTooltipContainer origin="top left" style={{ padding: '12px', minWidth: '320px' }}>
                <RoutingDiagram
                  currencyIn={currentTrade.inputAmount.currency}
                  currencyOut={currentTrade.outputAmount.currency}
                  routes={currentTrade.routingDiagramInfo}
                  trade={currentTrade}
                />
              </ResponsiveTooltipContainer>
            }
          >
            <ProviderChip>
              <LogoBox>
                <LogoRenderer logo={KXLogo} size={32} />
              </LogoBox>
              <Chevron $open={open} />
            </ProviderChip>
          </WithTooltip>
        )}
      </Header>

      <AnimatedDropdown open={open}>
        {trade && (
          <Body>
            <DetailRows>
              <Detail
                label={isExactIn ? 'Minimum received' : 'Maximum sent'}
                help={
                  isExactIn
                    ? "The minimum amount you're guaranteed to receive. If market conditions change unfavorably, your transaction will revert."
                    : "The maximum amount you'll be charged. If market conditions change unfavorably, your transaction will revert."
                }
              >
                <CurrencyLogo currency={isExactIn ? outputToken : inputToken} size="14px" />
                {formatSwapAmount(
                  isExactIn ? trade.minimumAmountOut(allowedSlippage) : trade.maximumAmountIn(allowedSlippage)
                )}
              </Detail>
              <Detail
                label="Slippage tolerance"
                help="Your transaction will revert if the price changes unfavorably by more than this percentage."
              >
                {allowedSlippage.toFixed(2)}%
              </Detail>
              <Detail
                label="Frontend fee"
                help="Optional fee that trading interfaces can charge users. Kodiak takes 0% interface fees - you only pay for gas and any underlying protocol fees."
              >
                {formatRefFee(currentTrade?.refFee, outputToken?.decimals)}
              </Detail>
            </DetailRows>

            {priceImpact && warningSeverity(priceImpact) > 3 && (
              <Warning>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                High price impact: low liquidity or a large trade size may return significantly fewer tokens.
              </Warning>
            )}

            {bestTrade && (
              <>
                <SectionTitle>
                  Routes
                  <QuestionHelper
                    text={
                      <TooltipParagraphs>
                        <p>
                          kX requests quotes from several aggregators and executes the one that leaves you the most
                          after gas, all with a single approval.
                        </p>
                        <p>
                          Percentages compare each source with the kX route on the same basis, including a small
                          adjustment for each source&apos;s reliability.
                        </p>
                        <p>
                          The kX route is simulated on-chain before it is shown. Other sources are listed as quoted.
                        </p>
                        <LearnMore
                          href="https://documentation.kodiak.finance/kx"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Learn more ↗
                        </LearnMore>
                      </TooltipParagraphs>
                    }
                  />
                </SectionTitle>
                <ProviderList>
                  <ProviderRow $best>
                    {/* The kX mark is wide and short, so it needs a larger box to match the square logos */}
                    <LogoRenderer logo={KXLogo} size={26} />
                    <ViaProvider>via {bestTrade.name}</ViaProvider>
                    <Diff $tone="better">Best</Diff>
                    <RouteOutput trade={bestTrade} />
                  </ProviderRow>
                  {competitorRows.map(({ trade: competitor, available }) => (
                    <ProviderRow key={competitor.id} $inactive={!available}>
                      <LogoRenderer logo={competitor.logo} size={22} />
                      <NameLine>{competitor.name}</NameLine>
                      {available ? (
                        <DiffLabel trade={competitor} bestTrade={bestTrade} />
                      ) : (
                        <Diff $tone="muted">Unavailable</Diff>
                      )}
                      <RouteOutput trade={competitor} />
                    </ProviderRow>
                  ))}
                </ProviderList>
              </>
            )}

            {currentTrade && hasRoute && (
              <RoutingDiagram
                currencyIn={currentTrade.inputAmount.currency}
                currencyOut={currentTrade.outputAmount.currency}
                routes={currentTrade.routingDiagramInfo}
                trade={currentTrade}
              />
            )}
          </Body>
        )}
      </AnimatedDropdown>
    </Container>
  )
}
